import type { UISchema } from '@carboncopyco/editor-utils';
import { MinusCircleOutlineIcon } from '@carboncopyco/icons';
import classNames from 'classnames';
import type { JsonFieldSchema } from '../../types';
import { FieldLabel } from './FieldLabel';

export interface FieldPanelProps<S extends UISchema> {
  field: JsonFieldSchema<S>;
  onChange?: (field: JsonFieldSchema<S>) => void;
  onRemoveField?: (jsonPath: string) => void;
  editable?: boolean;
  children: React.ReactNode;
}

const RemoveIcon = ({ onClick }: { onClick: () => void }) => {
  return (
    <span
      className="group-hover:block hidden text-ds-primary-300 cursor-pointer ml-auto"
      onClick={onClick}
    >
      <MinusCircleOutlineIcon size={16} />
    </span>
  );
};

const EditableFieldPanel = ({
  children,
  editable,
}: Pick<FieldPanelProps<UISchema>, 'editable' | 'children'>) => {
  return (
    <div
      className={classNames(
        'group flex flex-col gap-2.5 p-1.5 rounded-md',
        editable &&
          'hover:bg-ds-natural-100 group-[.dragging]:bg-ds-natural-100'
      )}
    >
      {children}
    </div>
  );
};

export const FieldPanel = <S extends UISchema>({
  field,
  onChange,
  editable,
  onRemoveField,
  children,
}: FieldPanelProps<S>) => {
  return (
    <EditableFieldPanel editable={editable}>
      <span className="flex flex-1 max-w-full gap-1.5 items-center">
        <FieldLabel
          label={field.uiSchema.label}
          htmlFor={field.jsonPath}
          editable={editable}
          onChange={(label) =>
            onChange?.({
              ...field,
              uiSchema: {
                ...field.uiSchema,
                label,
              },
            })
          }
        />
        {editable && (
          <RemoveIcon onClick={() => onRemoveField?.(field.jsonPath)} />
        )}
      </span>
      {children}
    </EditableFieldPanel>
  );
};

FieldPanel.RemoveIcon = RemoveIcon;
FieldPanel.EditableFieldPanel = EditableFieldPanel;
FieldPanel.Label = FieldLabel;
